import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Snake.css';

const BOARD_SIZE = 20;
const INITIAL_SNAKE = [{ x: 10, y: 10 }];
const INITIAL_FOOD = { x: 5, y: 5 };

const Snake = () => {
  const [snake, setSnake] = useState(INITIAL_SNAKE);
  const [food, setFood] = useState(INITIAL_FOOD);
  const [direction, setDirection] = useState({ x: 0, y: 0 });
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);

  const generateFood = useCallback(() => {
    const newFood = {
      x: Math.floor(Math.random() * BOARD_SIZE),
      y: Math.floor(Math.random() * BOARD_SIZE)
    };
    return newFood;
  }, []);

  const resetGame = () => {
    setSnake(INITIAL_SNAKE);
    setFood(INITIAL_FOOD);
    setDirection({ x: 0, y: 0 });
    setGameOver(false);
    setScore(0);
    setGameStarted(false);
  };

  const startGame = () => {
    setGameStarted(true);
    setDirection({ x: 1, y: 0 });
  };

  const moveSnake = useCallback(() => {
    if (!gameStarted || gameOver) return;

    setSnake(currentSnake => {
      const newSnake = [...currentSnake];
      const head = { ...newSnake[0] };
      
      head.x += direction.x;
      head.y += direction.y;

      // Check wall collision
      if (head.x < 0 || head.x >= BOARD_SIZE || head.y < 0 || head.y >= BOARD_SIZE) {
        setGameOver(true);
        return currentSnake;
      }

      // Check self collision
      if (newSnake.some(segment => segment.x === head.x && segment.y === head.y)) {
        setGameOver(true);
        return currentSnake;
      }

      newSnake.unshift(head);

      // Check food collision
      if (head.x === food.x && head.y === food.y) {
        setScore(prev => prev + 10);
        setFood(generateFood());
      } else {
        newSnake.pop();
      }

      return newSnake;
    });
  }, [direction, food, gameStarted, gameOver, generateFood]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!gameStarted) return;
      
      switch (e.key.toLowerCase()) {
        case 'w':
          if (direction.y === 0) setDirection({ x: 0, y: -1 });
          break;
        case 's':
          if (direction.y === 0) setDirection({ x: 0, y: 1 });
          break;
        case 'a':
          if (direction.x === 0) setDirection({ x: -1, y: 0 });
          break;
        case 'd':
          if (direction.x === 0) setDirection({ x: 1, y: 0 });
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [direction, gameStarted]);

  useEffect(() => {
    const gameInterval = setInterval(moveSnake, 150);
    return () => clearInterval(gameInterval);
  }, [moveSnake]);

  const renderBoard = () => {
    const board = [];
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        let cellClass = 'snake-cell';
        
        if (snake.some(segment => segment.x === x && segment.y === y)) {
          cellClass += ' snake-body';
        } else if (food.x === x && food.y === y) {
          cellClass += ' food';
        }
        
        board.push(
          <div key={`${x}-${y}`} className={cellClass}></div>
        );
      }
    }
    return board;
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🐍 Snake Game</h1>
        <div className="game-instructions">
          Use WASD keys to move the snake. Eat food to grow and increase your score!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          Score: {score}
          {gameOver && <div style={{color: 'red'}}>Game Over!</div>}
        </div>
        
        <div className="game-play-area">
          <div className="snake-board">
            {renderBoard()}
          </div>
        </div>

        <div className="game-controls">
          {!gameStarted ? (
            <button className="btn btn-success" onClick={startGame}>
              Start Game
            </button>
          ) : (
            <button className="btn btn-primary" onClick={resetGame}>
              Reset Game
            </button>
          )}
          <Link to="/" className="btn btn-secondary">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Snake;